import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  Box,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip
} from '@mui/material';
import {
  History as HistoryIcon,
  Visibility,
  Download,
  ExpandMore,
  ExpandLess,
  Refresh
} from '@mui/icons-material';
import { API_ENDPOINTS } from '../config';

interface ProcessingSession {
  id: number;
  task_id: string;
  filename: string;
  file_size: number;
  total_items: number;
  successful_predictions: number;
  failed_predictions: number;
  processing_start_time: string;
  processing_end_time: string;
  total_processing_time_seconds: number;
  average_time_per_item: number;
  created_at: string;
}

interface Prediction {
  id: number;
  task_id: string;
  prediction_id: string;
  input_data: string;
  status: string;
  data_status: string;
  confidence: number;
  examples_used_count: number;
  best_similarity: number;
  processing_time_ms: number;
  timestamp: string;
  created_at: string;
}

interface HistoryProps {
  open: boolean;
  onClose: () => void;
  embedded?: boolean;
}

export const History: React.FC<HistoryProps> = ({ open, onClose, embedded = false }) => {
  const [sessions, setSessions] = useState<ProcessingSession[]>([]);
  const [totalSessions, setTotalSessions] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // For viewing session details
  const [selectedSession, setSelectedSession] = useState<ProcessingSession | null>(null);
  const [sessionPredictions, setSessionPredictions] = useState<Prediction[]>([]);
  const [predictionsPage, setPredictionsPage] = useState(0);
  const [predictionsRowsPerPage, setPredictionsRowsPerPage] = useState(25);
  const [totalPredictions, setTotalPredictions] = useState(0);
  const [predictionsLoading, setPredictionsLoading] = useState(false);
  const [detailsDialog, setDetailsDialog] = useState(false);

  // Load processing history
  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.HISTORY}?limit=${rowsPerPage}&offset=${page * rowsPerPage}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSessions(data.sessions);
        setTotalSessions(data.total_count);
      } else {
        throw new Error('Failed to load history');
      }
    } catch (err) {
      setError('Failed to load processing history');
      console.error('History load error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load predictions for a specific session
  const loadSessionPredictions = async (taskId: string) => {
    setPredictionsLoading(true);
    
    try {
      const response = await fetch(
        `${API_ENDPOINTS.HISTORY}/${taskId}/predictions?limit=${predictionsRowsPerPage}&offset=${predictionsPage * predictionsRowsPerPage}`
      );
      
      if (response.ok) {
        const data = await response.json();
        setSessionPredictions(data.predictions);
        setTotalPredictions(data.total_count);
      } else {
        throw new Error('Failed to load session predictions');
      }
    } catch (err) {
      setError('Failed to load session predictions');
      console.error('Session predictions load error:', err);
    } finally {
      setPredictionsLoading(false);
    }
  };

  // Format file size
  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  // Get status color
  const getStatusColor = (successful: number, total: number) => {
    const rate = successful / total;
    if (rate === 1) return 'success';
    if (rate > 0.8) return 'warning';
    return 'error';
  };

  // Parse input data to columns (same as main app)
  const parseInputDataToColumns = (inputData: string) => {
    const columns: { [key: string]: string } = {};
    const parts = inputData.split(' | ');
    
    for (const part of parts) {
      if (part.includes(': ')) {
        const [key, value] = part.split(': ', 2);
        columns[key.trim()] = value.trim();
      }
    }
    
    return columns;
  };

  // Utility function to map data status for display
  const mapDataStatusForDisplay = (dataStatus: string) => {
    if (dataStatus.toLowerCase() === 'no issues') {
      return 'Resolved';
    }
    return dataStatus;
  };

  // Utility function to map column headers for display
  const mapColumnHeaderForDisplay = (columnName: string) => {
    if (columnName === 'Data Collection Type') {
      return 'Issue Type';
    }
    return columnName;
  };

  // View session details
  const viewSessionDetails = (session: ProcessingSession) => {
    setSelectedSession(session);
    setPredictionsPage(0);
    setDetailsDialog(true);
    loadSessionPredictions(session.task_id);
  };

  // Load history when component mounts for embedded mode
  useEffect(() => {
    if (embedded) {
      loadHistory();
    }
  }, [embedded, page, rowsPerPage]);

  // Load history when dialog opens
  useEffect(() => {
    if (open && !embedded) {
      loadHistory();
    }
  }, [open, page, rowsPerPage, embedded]);

  // Load predictions when pagination changes
  useEffect(() => {
    if (selectedSession && detailsDialog) {
      loadSessionPredictions(selectedSession.task_id);
    }
  }, [predictionsPage, predictionsRowsPerPage]);

  const mainContent = (
    <>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Size</TableCell>
                  <TableCell>Items</TableCell>
                  <TableCell>Success Rate</TableCell>
                  <TableCell>Processing Time</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow key={session.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {session.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>{formatFileSize(session.file_size)}</TableCell>
                    <TableCell>{session.total_items}</TableCell>
                    <TableCell>
                      <Chip
                        label={`${session.successful_predictions}/${session.total_items}`}
                        color={getStatusColor(session.successful_predictions, session.total_items)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {session.total_processing_time_seconds.toFixed(1)}s
                      <Typography variant="caption" display="block">
                        ({session.average_time_per_item.toFixed(2)}s/item)
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(session.created_at)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => viewSessionDetails(session)}
                          >
                            <Visibility />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Excel">
                          <IconButton
                            size="small"
                            onClick={async () => {
                              try {
                                // Download Excel for this historical session
                                const downloadUrl = `${API_ENDPOINTS.HISTORY}/${session.task_id}/excel`;
                                const response = await fetch(downloadUrl);
                                
                                if (response.ok) {
                                  const blob = await response.blob();
                                  const url = window.URL.createObjectURL(blob);
                                  const a = document.createElement('a');
                                  a.style.display = 'none';
                                  a.href = url;
                                  a.download = `${session.filename}_${session.task_id}.xlsx`;
                                  document.body.appendChild(a);
                                  a.click();
                                  window.URL.revokeObjectURL(url);
                                  document.body.removeChild(a);
                                } else {
                                  console.error('Download failed:', response.status, response.statusText);
                                  setError(`Download failed: ${response.statusText}`);
                                }
                              } catch (error) {
                                console.error('Download error:', error);
                                setError(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`);
                              }
                            }}
                          >
                            <Download />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalSessions}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(parseInt(event.target.value, 10));
              setPage(0);
            }}
          />
        </>
      )}
    </>
  );

  return (
    <>
      {embedded ? (
        mainContent
      ) : (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
          <DialogTitle>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box display="flex" alignItems="center">
                <HistoryIcon sx={{ mr: 1 }} />
                Processing History
              </Box>
              <IconButton onClick={loadHistory} disabled={loading}>
                <Refresh />
              </IconButton>
            </Box>
          </DialogTitle>
          
          <DialogContent>
            {mainContent}
          </DialogContent>
          
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Session Details Dialog */}
      <Dialog 
        open={detailsDialog} 
        onClose={() => setDetailsDialog(false)} 
        maxWidth="xl" 
        fullWidth
      >
        <DialogTitle>
          Session Details: {selectedSession?.filename}
        </DialogTitle>
        
        <DialogContent>
          {selectedSession && (
            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Session Summary
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Download />}
                  onClick={async () => {
                    try {
                      // Download Excel for this historical session using the correct endpoint
                      const downloadUrl = `${API_ENDPOINTS.HISTORY}/${selectedSession.task_id}/excel`;
                      const response = await fetch(downloadUrl);
                      
                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.style.display = 'none';
                        a.href = url;
                        a.download = `${selectedSession.filename}_${selectedSession.task_id}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      } else {
                        console.error('Download failed:', response.status, response.statusText);
                        setError(`Download failed: ${response.statusText}`);
                      }
                    } catch (error) {
                      console.error('Download error:', error);
                      setError(`Download error: ${error instanceof Error ? error.message : 'Unknown error'}`);
                    }
                  }}
                >
                  Download Excel
                </Button>
              </Box>
              <Box display="flex" gap={4} mb={3}>
                <Typography variant="body2">
                  <strong>Task ID:</strong> {selectedSession.task_id}
                </Typography>
                <Typography variant="body2">
                  <strong>Total Items:</strong> {selectedSession.total_items}
                </Typography>
                <Typography variant="body2">
                  <strong>Success Rate:</strong> {selectedSession.successful_predictions}/{selectedSession.total_items}
                </Typography>
                <Typography variant="body2">
                  <strong>Processing Time:</strong> {selectedSession.total_processing_time_seconds.toFixed(2)}s
                </Typography>
              </Box>
            </Box>
          )}

          {predictionsLoading ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <TableContainer component={Paper}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ minWidth: 350 }}>PI Tag Name</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>Tag Description</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Asset Type</TableCell>
                      <TableCell sx={{ minWidth: 300 }}>Issue Description</TableCell>
                      <TableCell sx={{ minWidth: 200 }}>Predicted Status</TableCell>
                      <TableCell sx={{ minWidth: 220 }}>Predicted Data Status</TableCell>
                      <TableCell sx={{ minWidth: 180 }}>Prediction ID</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sessionPredictions.map((prediction) => {
                      const parsedColumns = parseInputDataToColumns(prediction.input_data);
                      return (
                        <TableRow key={prediction.id}>
                          <TableCell sx={{ minWidth: 350 }}>
                            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.875rem' }}>
                              {parsedColumns['Pi tag Name'] || 'N/A'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 300 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {(parsedColumns['PI Tag Description'] || 'N/A').substring(0, 150)}
                              {((parsedColumns['PI Tag Description']?.length || 0) > 150) && '...'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <Chip
                              label={parsedColumns['Asset Type'] || 'Unknown'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 300 }}>
                            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
                              {(parsedColumns['Issue Description'] || 'N/A').substring(0, 120)}
                              {((parsedColumns['Issue Description']?.length || 0) > 120) && '...'}
                            </Typography>
                          </TableCell>
                          <TableCell sx={{ minWidth: 200 }}>
                            <Chip 
                              label={prediction.status} 
                              size="small"
                              color={prediction.status.toLowerCase().includes('issues') ? 'error' : 'success'}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            <Chip 
                              label={mapDataStatusForDisplay(prediction.data_status)} 
                              size="small"
                              color={prediction.data_status.toLowerCase().includes('ongoing') ? 'error' : 'success'}
                            />
                          </TableCell>
                          <TableCell sx={{ minWidth: 180 }}>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {prediction.prediction_id.substring(0, 8)}...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component="div"
                count={totalPredictions}
                rowsPerPage={predictionsRowsPerPage}
                page={predictionsPage}
                onPageChange={(_, newPage) => setPredictionsPage(newPage)}
                onRowsPerPageChange={(event) => {
                  setPredictionsRowsPerPage(parseInt(event.target.value, 10));
                  setPredictionsPage(0);
                }}
              />
            </>
          )}
        </DialogContent>
        
        <DialogActions>
          <Button onClick={() => setDetailsDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}; 